import React from "react";
import { Box, Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { formatPolicyEmploymentStatus, formatProductType } from "../../../utils/enumFormatter";
import { formatCurrency } from "../../../utils/namesFormater";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { isoDateColumn } from "../../../shared/UIParts/columnsFormatter";

/**
 * Row shape and columns shared by the policy management table and the policy history table.
 * Rows keep `product` / `data` so the shared column formatters (fees, status, path tooltip) can read the policy.
 */

export const buildPolicyRow = (row: MimshakData, index: number) => {
  const product = row.data?.products?.[0] || null;
  const policy = product?.policies_or_accounts?.[0];

  return {
    id: index,
    contact_id: row.contact_id,
    handler_code: row.handler_code,
    reported_period: row.reported_period,
    product_type: formatProductType(row.product_type),
    raw_product_type: row.product_type,
    funds_sum: row.funds_sum,
    policy_number: row.policy_number,
    deposit_amount: row.deposit_amount,
    withdrawal_amount: row.withdrawal_amount,
    recurrent_deposit: row.recurrent_deposit,
    transfer_out: row.transfer_out,
    transfer_in: row.transfer_in,
    transfer_out_to: row.transfer_out_to ?? "",
    agent_appointment_date: row.agent_appointment_date,
    new_policy: row.new_policy,
    adjusted_product_type: product?.details?.adjusted_product_type ?? row.product_type,
    policy_plan_or_account_type: policy ? formatPolicyEmploymentStatus(policy.plan_or_account_type) : "-",
    policy_agent_id: policy?.agent_id_for_producer ?? "-",
    policy_join_date: policy?.join_date ?? "-",
    product_join_date: row.p_join_date ?? policy?.product_join_date ?? "-",
    fund_ids: row.fund_ids ?? [],
    product,
    data_access_type: row.data_access_type,
    data: row.data,
  };
};

export type PolicyRow = ReturnType<typeof buildPolicyRow>;

/** הפקדות / משיכות — deposit (with a standing-order chip) and withdrawal */
export const depositsWithdrawalsColumn = (): GridColDef => ({
  field: "deposit_amount",
  headerName: "הפקדות / משיכות",
  width: 110,
  renderCell: (params: any) => {
    if (params.row.__isSummary) return null;
    const dep = params.row.deposit_amount;
    const wit = params.row.withdrawal_amount;
    const rec = params.row.recurrent_deposit;
    const hasDep = dep && dep !== 0;
    const hasWit = wit && wit !== 0;
    if (!hasDep && !hasWit) return null;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
        {hasDep && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <span dir="ltr">{formatCurrency(dep)}</span>
            {rec && rec !== 0 && (
              <Chip
                label="ה.קבע"
                size="small"
                sx={{ height: 18, fontSize: 11, bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 700, "& .MuiChip-label": { px: 0.75 } }}
              />
            )}
          </Box>
        )}
        {hasWit && <span dir="ltr" style={{ color: "#d32f2f" }}>-{formatCurrency(wit)}</span>}
      </Box>
    );
  },
});

/** ניוד — transferred out (and to where) / transferred in */
export const transfersColumn = (): GridColDef => ({
  field: "transfer_out",
  headerName: "ניוד",
  width: 150,
  renderCell: (params: any) => {
    if (params.row.__isSummary) return null;
    const out = params.row.transfer_out;
    const inVal = params.row.transfer_in;
    const to = params.row.transfer_out_to;
    const hasOut = out && out !== 0;
    const hasIn = inVal && inVal !== 0;
    if (!hasOut && !hasIn) return null;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.4 }}>
        {hasOut && (
          <>
            <span style={{ fontSize: 11, color: "#555" }}>הועבר ל{to ? ` ${to}` : ""}</span>
            <span dir="ltr" style={{ fontSize: 12, fontWeight: 600, color: "#d32f2f" }}>{formatCurrency(out)}</span>
          </>
        )}
        {hasIn && (
          <>
            <span style={{ fontSize: 11, color: "#555" }}>התקבל</span>
            <span dir="ltr" style={{ fontSize: 12, fontWeight: 600, color: "#2e7d32" }}>{formatCurrency(inVal)}</span>
          </>
        )}
      </Box>
    );
  },
});

/** תאריך הצטרפות למוצר */
export const productJoinDateColumn = (): GridColDef => isoDateColumn("product_join_date", "תאריך הצטרפות למוצר", 110);
